import { useEffect, useState } from "react";
import { AlertTriangle, Building2, FileUp, Loader2, Tag, BadgeDollarSign } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { NfeItemRow } from "@/components/stock/NfeItemRow";
import { LabelsDialog } from "@/components/stock/LabelsDialog";
import { RepriceDialog, repriceCandidates } from "@/components/stock/RepriceDialog";
import { useAuth } from "@/context/AuthContext";
import { api, brl, canDo, errMsg, fmtDate, fmtDateTime } from "@/lib/api";

const initRow = (it) => ({ ...it, note_name: it.name, factor: 1, action: it.match ? "existing" : "new_product", kind: it.match?.kind || "product", item_id: it.match?.id || "", price: "", category_id: "" });

function NfeUpload({ busy, onFile, onLabels, onReprice }) {
  const [recent, setRecent] = useState([]);
  useEffect(() => { api.get("/stock/nfe/imports").then((r) => setRecent(r.data)).catch(() => {}); }, []);
  const open = (id, fn) => api.get(`/stock/nfe/imports/${id}`).then((r) => fn(r.data)).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-5">
      <label className="flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed p-10 text-center transition-colors hover:border-primary hover:bg-primary/5" data-testid="nfe-dropzone">
        {busy ? <Loader2 className="h-8 w-8 animate-spin text-primary" /> : <FileUp className="h-8 w-8 text-primary" />}
        <span className="font-medium">{busy ? "Lendo a nota…" : "Escolha o arquivo XML da NF-e"}</span>
        <span className="max-w-md text-xs text-muted-foreground">O sistema lê o fornecedor, os produtos, as quantidades e os valores. Você confere tudo antes de confirmar a entrada.</span>
        <input type="file" accept=".xml,text/xml,application/xml" className="hidden" disabled={busy} onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; if (file) onFile(file); }} data-testid="nfe-file-input" />
      </label>
      {recent.length > 0 && (
        <div data-testid="nfe-recent-imports">
          <p className="mb-2 text-sm font-medium">Últimas notas importadas</p>
          <div className="divide-y rounded-lg border text-sm">{recent.slice(0, 5).map((n) => (
            <div key={n.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"><span>NF-e {n.number} · {n.supplier_name || "—"}</span>
              <span className="flex items-center gap-2 text-muted-foreground">{fmtDateTime(n.created_at)} · {n.items_count} itens
                {onReprice && <Button size="sm" variant="ghost" onClick={() => open(n.id, onReprice)} data-testid={`nfe-recent-reprice-${n.id}`}><BadgeDollarSign className="mr-1 h-3.5 w-3.5" />Remarcar</Button>}
                <Button size="sm" variant="ghost" onClick={() => open(n.id, onLabels)} data-testid={`nfe-recent-labels-${n.id}`}><Tag className="mr-1 h-3.5 w-3.5" />Etiquetas</Button></span></div>
          ))}</div>
        </div>
      )}
    </div>
  );
}

function NfeHeader({ nfe }) {
  const s = nfe.supplier;
  return (
    <div className="space-y-3">
      {nfe.already_imported && <div className="flex gap-2 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500" data-testid="nfe-already-imported"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />Esta nota já foi importada antes. Para não duplicar o estoque, ela não pode ser importada de novo.</div>}
      <div className="grid gap-3 rounded-xl border p-4 sm:grid-cols-[1fr_auto]">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-semibold"><Building2 className="h-4 w-4 shrink-0 text-primary" /><span className="truncate" data-testid="nfe-supplier-name">{s.name || "Fornecedor não informado"}</span></p>
          <p className="text-xs text-muted-foreground">{[s.document && `CNPJ/CPF ${s.document}`, s.trade_name].filter(Boolean).join(" · ")}</p>
          <p className={`mt-1 text-xs ${s.existing ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`} data-testid="nfe-supplier-status">{s.existing ? `Fornecedor já cadastrado: ${s.existing.name}` : "Fornecedor novo: será cadastrado ao confirmar"}</p>
        </div>
        <div className="text-sm sm:text-right">
          <p className="font-mono font-semibold" data-testid="nfe-number">NF-e {nfe.number}{nfe.series && ` · série ${nfe.series}`}</p>
          {nfe.issued_at && <p className="text-xs text-muted-foreground">Emitida em {fmtDate(nfe.issued_at)}</p>}
          <p className="font-mono text-lg font-bold" data-testid="nfe-total">{brl(nfe.total)}</p>
        </div>
      </div>
    </div>
  );
}

const missing = (r) => (r.action === "existing" && !r.item_id) || (r.action === "new_product" && !(Number(r.price) > 0)) || (r.action.startsWith("new") && !r.name.trim()) || (r.action !== "skip" && !(Number(r.factor) > 0));
const toItem = (r) => ({ name: (r.name || r.note_name).trim() || r.note_name, ean: r.ean, unit: r.unit, qty: r.qty, unit_cost: r.unit_cost, factor: Number(r.factor) || 1, expiry: r.expiry || "", action: r.action, kind: r.kind, item_id: r.item_id, price: Number(r.price) || 0, category_id: r.category_id || null });

function NfeImportDialog({ onClose, onDone, onLabels, onReprice }) {
  const [nfe, setNfe] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState({ products: [], ingredients: [], categories: [] });
  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/ingredients"), api.get("/categories")])
      .then(([p, i, c]) => setCatalog({ products: p.data, ingredients: i.data, categories: c.data })).catch(() => {});
  }, []);
  const read = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { const { data } = await api.post("/stock/nfe/parse", fd); setNfe(data); setRows(data.items.map(initRow)); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const todo = rows.filter((r) => r.action !== "skip");
  const confirm = async () => {
    if (!todo.length) return toast.error("Escolha ao menos um item para dar entrada");
    const miss = todo.find(missing);
    if (miss) return toast.error(`Complete os dados do item: ${miss.note_name}${miss.action === "new_product" ? " (nome e preço de venda)" : ""}`);
    setBusy(true);
    try {
      const { key, number, series, issued_at, total, supplier } = nfe;
      const { data } = await api.post("/stock/nfe/import", { key, number, series, issued_at, total, supplier, items: rows.map(toItem) });
      toast.success(`NF-e ${data.number}: entrada de ${data.items_count} itens registrada`);
      onDone(data);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const patch = (i) => (p) => setRows((rs) => rs.map((x, k) => (k === i ? { ...x, ...p } : x)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="nfe-import-dialog">
        <DialogHeader><DialogTitle>Importar NF-e (XML)</DialogTitle></DialogHeader>
        {!nfe ? <NfeUpload busy={busy} onFile={read} onLabels={onLabels} onReprice={onReprice} /> : (
          <div className="space-y-4">
            <NfeHeader nfe={nfe} />
            <p className="text-sm text-muted-foreground">Confira cada item. Os já cadastrados foram reconhecidos pelo código de barras ou pelo nome; os demais podem ser cadastrados como produto ou insumo.</p>
            <div className="space-y-3">{rows.map((r, i) => <NfeItemRow key={`${r.n}-${i}`} r={r} idx={i} catalog={catalog} onChange={patch(i)} />)}</div>
          </div>
        )}
        {nfe && (
          <DialogFooter className="items-center gap-2 sm:justify-between">
            <span className="text-sm text-muted-foreground" data-testid="nfe-summary">{todo.length} de {rows.length} itens entram no estoque · {todo.filter((r) => r.action.startsWith("new")).length} novos cadastros</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setNfe(null); setRows([]); }} disabled={busy} data-testid="nfe-change-file-btn">Trocar arquivo</Button>
              <Button onClick={confirm} disabled={busy || nfe.already_imported} data-testid="nfe-confirm-btn">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Confirmar entrada</Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function NfeImportButton({ onDone }) {
  const { session } = useAuth();
  const canEdit = canDo(session.user, "edit_products");
  const [open, setOpen] = useState(false);
  const [labels, setLabels] = useState(null);
  const [reprice, setReprice] = useState(null);
  const showLabels = (doc, quiet) => {
    const lines = (doc.lines || []).filter((l) => l.kind === "product");
    if (lines.length) setLabels({ title: `Etiquetas da NF-e ${doc.number}`, lines });
    else if (!quiet) toast.info("Esta nota não tem produtos de venda para etiquetar");
  };
  const afterImport = (doc, manual) => {
    setOpen(false);
    if (canEdit && repriceCandidates(doc).length) setReprice(doc);
    else if (manual) toast.info("Nenhum produto teve o custo alterado nesta nota");
    else showLabels(doc, true);
  };
  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="nfe-import-btn"><FileUp className="mr-1 h-4 w-4" />Importar NF-e (XML)</Button>
      {open && <NfeImportDialog onClose={() => setOpen(false)} onLabels={(doc) => { setOpen(false); showLabels(doc); }}
        onReprice={canEdit ? (doc) => afterImport(doc, true) : undefined} onDone={(doc) => { onDone(); afterImport(doc, false); }} />}
      {reprice && <RepriceDialog doc={reprice} onDone={() => { const d = reprice; setReprice(null); onDone(); showLabels(d, true); }} />}
      {labels && <LabelsDialog {...labels} onClose={() => setLabels(null)} />}
    </>
  );
}
